'use client';

import { motion } from 'motion/react';
import { useSectionMotionVariants } from '@/components/shared/motion';
import { useScrollRevealMotion } from '@/hooks/use-scroll-reveal-motion';
import SectionHeader from '@/components/shared/section-header';
import ProjectCard from '@/components/sections/portfolio/card';
import { getProjectDisplayImage, type SanityProject } from '@/sanity/project-data';
import type { CompletedProjectsSectionContent } from '@/sanity/lib/sections';
import { SanityInlineText } from '@/components/shared/sanity-text';
import { SectionCtaPair, sectionCtaPairFromContent } from '@/components/shared/section-cta-pair';
import { SectionLayout } from '../../shared/section-layout';

export default function CompletedProjects({
  projects,
  content,
}: {
  projects: SanityProject[];
  content?: CompletedProjectsSectionContent | null;
}) {
  const {
    sectionCardRevealVariants,
    sectionGridRevealVariants,
    sectionSequenceBlockVariants,
    sectionSequenceRevealVariants,
  } = useSectionMotionVariants();
  const scrollReveal = useScrollRevealMotion();
  const projectsWithImages = projects.filter((project) => getProjectDisplayImage(project));
  const { header, ctaButtons } = content ?? {};
  const showHeader = content?.showHeader !== false;
  const { primary, secondary } = sectionCtaPairFromContent(content?.showCTA, ctaButtons);

  if (!content || content.show === false || !projectsWithImages.length) return null;

  return (
    <SectionLayout
      id="our-portfolio"
      className="relative scroll-mt-14 sm:scroll-mt-0 bg-neutral-900 dark:bg-black py-6 sm:py-12 xl:py-16 2xl:py-24"
      containerClassName="mx-auto max-w-7xl px-4 sm:px-6 min-[1324px]:px-0"
    >
      <motion.div
        className="space-y-6 sm:space-y-12"
        {...scrollReveal}
        variants={sectionSequenceRevealVariants}
      >
        {showHeader ? (
          <SectionHeader
            sequenceChild
            content={{
              eyebrow: header?.tagline,
              description: header?.subheading,
            }}
            titleSlot={
              header?.heading ? (
                <h2 className="text-3xl sm:text-5xl leading-tight font-light tracking-tight text-white">
                  <SanityInlineText
                    blocks={header.heading}
                    emphasisClassName="italic font-normal text-[#FCFAD7]"
                  />
                </h2>
              ) : undefined
            }
            eyebrowClassName="text-[#FCFAD7]"
            emphasisClassName="text-[#FCFAD7]"
            headingClassName="text-white!"
            subheadingClassName="text-muted-foreground"
          />
        ) : null}
        {primary ? (
          <motion.div variants={sectionSequenceBlockVariants}>
            <SectionCtaPair
              primary={primary}
              secondary={secondary}
              trackingLocation="portfolio"
              className="max-xl:px-6"
            />
          </motion.div>
        ) : null}
        <motion.div
          variants={sectionGridRevealVariants}
          className="grid gap-3 md:grid-cols-2 xl:grid-cols-3"
        >
          {projectsWithImages.map((project) => (
            <motion.div key={project._id} variants={sectionCardRevealVariants}>
              <ProjectCard project={project} />
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </SectionLayout>
  );
}
